import { useMemo } from "react"
import styled from "styled-components"

import { ChevronDownIcon } from "@minuthq/meatball-ui-icons"
import { Button, spacing, Text } from "@minuthq/meatball-ui-react"
import { format } from "date-fns"

import { TTimezone } from "src/data/timezones/timezones"
import { useTranslate } from "src/i18n/useTranslate"
import { TComboboxBaseOption } from "src/ui/Combobox/Combobox"
import { Container } from "src/ui/Container/Container"
import { getDatePickerTimeFormat } from "src/ui/DateRangePicker/datePickerLogic"
import {
  DateRangePickerContainer,
  DateRangePickerContainerProps,
} from "src/ui/DateRangePicker/DateRangePickerContainer"
import { DropdownSelect } from "src/ui/DropdownSelect/DropdownSelect"
import {
  isDateBlocked,
  isTimeBlocked,
  useDateTimePickerStateWithTimezone,
} from "src/ui/MDateTimePicker/dateTimePickerUtils"
import {
  convertInputValueToOptionLabel,
  getTimePickerOptions,
  isTMTimePickerInputValue,
} from "src/ui/MTimePickerInput/timePickerUtils"
import { ErrorService } from "src/utils/ErrorService"

type TDateTimePickerProps = {
  initialDate?: Date | null
  datePickerProps?: Partial<DateRangePickerContainerProps>
  timePickerInputProps?: Partial<{
    clockType?: "12" | "24"
    interval?: number
  }>
  onChange: (date: Date | null) => void
  timePrefixText?: string
  suffixText?: string
  isDateTimeBlocked?: (date: Date) => boolean
  showRemoveButton?: boolean
  removeButtonLabel?: string
  timezone?: string | TTimezone
}

export function MDateTimePickerText({
  initialDate,
  datePickerProps,
  timePickerInputProps,
  onChange,
  timePrefixText,
  suffixText,
  isDateTimeBlocked,
  showRemoveButton = false,
  removeButtonLabel,
  timezone,
}: TDateTimePickerProps) {
  const { t, langKeys } = useTranslate()

  const {
    dateInLocalTime,
    setDateInLocalTime,
    timeInLocalTimezone,
    setTimeInLocalTimezone,
  } = useDateTimePickerStateWithTimezone(onChange, initialDate, timezone)
  const { dateRangeProps, ...rest } = datePickerProps ?? {}

  const isTimeBlockedFn = isTimeBlocked(
    dateInLocalTime,
    isDateTimeBlocked,
    timezone
  )
  const isDateBlockedFn = isDateBlocked(
    timeInLocalTimezone,
    isDateTimeBlocked,
    timezone
  )

  const options: TComboboxBaseOption[] = useMemo(() => {
    return getTimePickerOptions({
      interval: timePickerInputProps?.interval,
      start: 0,
      clockType: timePickerInputProps?.clockType,
      isTimeBlocked: isTimeBlockedFn,
    })
  }, [
    timePickerInputProps?.clockType,
    timePickerInputProps?.interval,
    isTimeBlockedFn,
  ])

  const selectedValue = timeInLocalTimezone

  const displayLabel = selectedValue
    ? convertInputValueToOptionLabel(
        selectedValue,
        timePickerInputProps?.clockType ?? "24"
      )
    : t(langKeys.time)

  function handleChange(value: string) {
    if (!isTMTimePickerInputValue(value)) {
      ErrorService.captureMessage("Invalid time value", {
        context: { data: { value, selectedValue, options } },
      })
      setTimeInLocalTimezone(null)
      return
    }
    setTimeInLocalTimezone(value)
  }

  return (
    <Container>
      <Box>
        <DateRangePickerContainer
          startDate={dateInLocalTime}
          endDate={dateInLocalTime}
          onDateRangeChange={({ startDate }) => {
            setDateInLocalTime(startDate)
          }}
          hideActionbar={!showRemoveButton}
          customActions={
            showRemoveButton
              ? [
                  {
                    id: "remove",
                    label: removeButtonLabel ?? t(langKeys.remove),
                    onClick: () => setDateInLocalTime(null),
                  },
                ]
              : undefined
          }
          defaultPresetKey="MONTH"
          hidePresets={["DAY", "WEEK", "MONTH", "YEAR", "CLEAR_SELECTION"]}
          behavior="date"
          futureDatesSelectable={true}
          renderButton={({ onClick, state }) => {
            return (
              <Button
                variant="minimal"
                onClick={(e) => {
                  onClick(e.currentTarget)
                }}
                endIcon={<ChevronDownIcon width={10} />}
              >
                <Text variant="subtitleS" color="unset">
                  {getDispalyValue(state.startDate) || t(langKeys.date)}
                </Text>
              </Button>
            )
          }}
          dateRangeProps={{
            numberOfMonths: 1,
            isDateBlocked: isDateBlockedFn,
            ...dateRangeProps,
          }}
          calendarPlacement="bottom-start"
          {...rest}
        />

        {timePrefixText && <Text>{timePrefixText}</Text>}

        <DropdownSelect
          label={displayLabel}
          options={options}
          selectedValue={selectedValue}
          onChange={({ option }) => handleChange(option.value)}
          mButtonVariant="minimal"
        />

        {suffixText && <Text>{suffixText}</Text>}
      </Box>
    </Container>
  )
}

function getDispalyValue(date: Date | null) {
  const formatType = getDatePickerTimeFormat(date, new Date())
  const dateRangeStr = date ? `${format(date, formatType)}` : ``
  return dateRangeStr
}

const Box = styled.div`
  display: flex;
  gap: ${spacing.XS};
  align-items: center;
`
